import React, { useState } from "react";
import Button from "../../Components/Utils/Button/Button";
import { getFileIcon } from "../../Helpers/fileIcon";
import { formatBytes, LibraryFileDetails } from "../../API/Library/LibraryCommands";
import { formatFullDate, formatRelativeDate, groupLinkedEvidence, initials, libraryDisplayTitle, libraryKindNoun, libraryProductLabel } from "./libraryUtils";
import styles from "./Library.module.css";

type FileDetailsPaneProps = {
  details: LibraryFileDetails | null;
  loading: boolean;
  canEdit: boolean;
  onClose: () => void;
  onPreview: () => void;
  onEditLinks: () => void;
  onAddTag: (tag: string) => Promise<void>;
  onRemoveTag: (tag: string) => Promise<void>;
  onAddComment: (comment: string) => Promise<void>;
};

const FileDetailsPane = ({
  details,
  loading,
  canEdit,
  onClose,
  onPreview,
  onEditLinks,
  onAddTag,
  onRemoveTag,
  onAddComment,
}: FileDetailsPaneProps) => {
  const [tagDraft, setTagDraft] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const file = details?.file;

  const addTag = async () => {
    const tag = tagDraft.trim();
    if (!tag || !canEdit || saving) return;
    if ((file?.tags ?? []).some((item) => item.toLowerCase() === tag.toLowerCase())) {
      setTagDraft("");
      return;
    }
    setSaving(true);
    try {
      await onAddTag(tag);
      setTagDraft("");
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    const text = comment.trim();
    if (!text || !canEdit || saving) return;
    setSaving(true);
    try {
      await onAddComment(text);
      setComment("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className={styles.detailsPane} aria-label="File details">
      <div className={styles.detailsHeader}>
        <h2>Details</h2>
        <button type="button" className={styles.iconButton} onClick={onClose} aria-label="Close details">
          <img src="/img/close.svg" alt="" />
        </button>
      </div>
      {loading && !file && (
        <div className={styles.detailsBody}>
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonLine} style={{ width: "70%" }} />
          <div className={styles.skeletonLine} style={{ width: "46%" }} />
          <div className={styles.skeletonLine} style={{ width: "58%" }} />
        </div>
      )}
      {file && (
        <div className={styles.detailsBody}>
          <img className={styles.detailsIcon} src={getFileIcon(file.title)} alt="" />
          <h3 className={styles.detailsTitle}>{file.title}</h3>
          <p className={styles.meta}>{formatBytes(file.fileSize)}</p>
          <p className={styles.meta}>{file.uploadedByName || "Unknown"} · {formatRelativeDate(file.createdAt)}</p>
          <p className={styles.meta}>{formatFullDate(file.createdAt)}</p>
          <Button clickHandler={onPreview}>Preview</Button>

          <section className={styles.detailsSection}>
            <h4>Tags</h4>
            <div className={styles.chips}>
              {(file.tags ?? []).map((tag) => (
                <span key={tag} className={styles.chip}>
                  {tag}
                  {canEdit && (
                    <button type="button" className={styles.chipRemove} onClick={() => onRemoveTag(tag)} aria-label={`Remove ${tag}`}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            {canEdit && (
              <input
                value={tagDraft}
                placeholder="Add tag"
                aria-label="Add tag"
                disabled={saving}
                onChange={(event) => setTagDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addTag();
                  }
                }}
              />
            )}
          </section>

          <section className={styles.detailsSection}>
            <div className={styles.detailsSectionHead}>
              <h4>Linked evidence</h4>
              <button type="button" className={styles.textLink} onClick={onEditLinks}>Edit links</button>
            </div>
            {(details?.links ?? []).length === 0 && <p className={styles.meta}>Nothing linked yet.</p>}
            <div className={styles.evidenceList}>
              {groupLinkedEvidence(details?.links ?? []).map((group) => (
                <div key={group.key} className={styles.evidenceGroup}>
                  <div className={styles.evidenceGroupHead}>
                    <strong title={group.areaTitle}>{libraryDisplayTitle(group.areaTitle) || group.areaTitle}</strong>
                    {group.type && <span className={styles.pill}>{libraryProductLabel(group.type)}</span>}
                  </div>
                  {group.sections.length === 0 && (
                    <p className={styles.meta}>Linked to this {libraryKindNoun(group.type)}</p>
                  )}
                  {group.sections.map((section) => (
                    <div key={section.title || "questions"} className={styles.evidenceSection}>
                      {section.title && <h5 title={section.title}>{section.title}</h5>}
                      <ul className={styles.evidenceQuestions}>
                        {section.questions.map((question) => {
                          const title = libraryDisplayTitle(question.questionTitle, question.questionId);
                          if (!title) return null;
                          return (
                            <li key={question.questionId} title={title}>{title}</li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className={styles.detailsSection}>
            <h4>Comments</h4>
            <div className={styles.commentThread}>
              {(details?.comments ?? []).map((item) => (
                <div key={item.id} className={styles.comment}>
                  <span className={styles.avatar} aria-hidden>{initials(item.commenter)}</span>
                  <div>
                    <div className={styles.commentMeta}>
                      <strong>{item.commenter || "Someone"}</strong>
                      <span>{formatRelativeDate(item.dateMade)}</span>
                    </div>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
            {canEdit && (
              <>
                <textarea
                  value={comment}
                  placeholder="Add a comment"
                  aria-label="Add a comment"
                  onChange={(event) => setComment(event.target.value)}
                />
                <div className={styles.dialogActions}>
                  <Button disabled={!comment.trim() || saving} clickHandler={submitComment}>Post</Button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </aside>
  );
};

export default FileDetailsPane;
